// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This subpath only uses the badge and portal tooltip; its GraphQL import is type-only.
import {CommentAuthorAssociation} from '@github-ui/commenting/CommentAuthorAssociation'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useAnalytics} from '@github-ui/use-analytics'
import {CommentIcon, IssueOpenedIcon, MilestoneIcon, SmileyIcon} from '@primer/octicons-react'
import {Label, Link} from '@primer/react'
import {SkeletonBox} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {memo, type ComponentProps, type ElementType, type ReactNode} from 'react'
import styles from './PullsListItem.module.css'
import {AssigneeAvatars} from '../shared/AssigneeAvatars'
import compactStyles from '../shared/CompactListItem.module.css'
import {renderLabelBadges} from '../shared/LabelBadges'
import {unreadRowClassName, UnreadRowLabel} from '../shared/UnreadRowIndicator'
import inlineCodeStyles from '../shared/TitleInlineCode.module.css'
import {DisplayStateType, ReviewDecisionState, type EnrichedPullListItem} from './types/list-types'
import {
  AuthorFilterControl,
  PullsListItemTimestampContainer,
  type AuthorFilterLinkProps,
} from './PullsListItemTimestampContainer'
import {
  CompactReviewDecision,
  CompactStatusCheckDisplay,
  ReviewDecision,
  StatusCheckDisplay,
} from './helpers/list-helpers'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {PullsChecksStatusBadge} from './PullsChecksStatusBadge'
import {StackBadge} from '@github-ui/stack-badge'
import {deriveAttributedUsername, isAgentAuthor} from './helpers/derive-attributed-username'
import {NonInteractiveTooltip} from './helpers/NonInteractiveTooltip'
import {PR_STATE_FALLBACK_TOOLTIP_LABEL, PR_STATE_TOOLTIP_LABELS} from './consts/pr-state-consts'
import {getReactionSortDisplay} from '../shared/consts/sorting-consts'

export type PullsListItemLinkProps = Omit<
  NonNullable<ComponentProps<typeof ListItemTitle>['linkProps']>,
  'href' | 'onClick'
>

const AUTHOR_IDENTITY_LABELS = {BOT: 'Bot', AI: 'AI', MANNEQUIN: 'mannequin'} as const

interface PullsListItemProps {
  as?: ElementType
  role?: string
  pull: EnrichedPullListItem
  showUnreadIndicator?: boolean
  showUnreadBorder?: boolean
  isSelected?: boolean
  onSelect?: (isSelected: boolean, isRangeSelection?: boolean) => void
  onSelectRow?: () => void
  onFilterSelect?: (type: 'repo' | 'author' | 'label' | 'assignee', value: string) => void
  onReviewFilterSelect?: (value: 'approved' | 'required' | 'changes_requested') => void
  isDeferredLoading?: boolean
  isAgentAttributionEnabled?: boolean
  sortingItemSelected?: string
  showRepository?: boolean
  titleLinkProps?: PullsListItemLinkProps
  getAuthorLinkProps?: (login: string) => AuthorFilterLinkProps | undefined
  declutter?: boolean
  isUpdatedSort?: boolean
}

function renderCompactMetadataValue(value: ReactNode) {
  return value === null || value === undefined || value === '' ? (
    <span className={compactStyles.emptyPlaceholder}>&mdash;</span>
  ) : (
    value
  )
}

const Middot = () => <span aria-hidden="true">&middot;</span>

function DeferredMetadataSkeleton({kind, width}: {kind: 'review' | 'status'; width: string}) {
  return (
    <span className={styles.metadataGroup} aria-hidden="true">
      <Middot />
      <SkeletonBox
        className={styles.inlineSkeleton}
        data-testid={`deferred-${kind}-skeleton`}
        height="12px"
        width={width}
      />
    </span>
  )
}

function DeferredCompactMetadataSkeleton({kind, width}: {kind: 'review' | 'status'; width: string}) {
  return (
    <span className={styles.compactSkeletonSlot} aria-hidden="true">
      <SkeletonBox
        className={styles.inlineSkeleton}
        data-testid={`deferred-${kind}-skeleton`}
        height="12px"
        width={width}
      />
    </span>
  )
}

function QueuedState({mergeQueueUrl}: {mergeQueueUrl?: string}) {
  return mergeQueueUrl ? (
    <Link href={mergeQueueUrl} aria-label="Queued, view merge queue" inline muted className="Link--inTextBlock">
      Queued
    </Link>
  ) : (
    <>Queued</>
  )
}

function LinkedIssueMetadataValue({
  count,
  url,
  pullRequestRepository,
  pullRequestNumber,
  pullRequestTitle,
}: {
  count: number | undefined
  url: string | undefined
  pullRequestRepository: string
  pullRequestNumber: number
  pullRequestTitle: string
}) {
  if (!count || count < 1) return null

  const label = `${count} linked ${count === 1 ? 'issue' : 'issues'}`
  const linkLabel = `${label} for pull request ${pullRequestRepository}#${pullRequestNumber}: ${pullRequestTitle}`
  const content = (
    <>
      <IssueOpenedIcon aria-hidden="true" size={16} />
      <span aria-hidden="true">{count}</span>
    </>
  )

  return count === 1 && url ? (
    <Link
      href={url}
      aria-label={linkLabel}
      muted
      className={clsx('d-inline-flex flex-items-center gap-1', styles.linkedIssueLink)}
      onClick={event => event.stopPropagation()}
    >
      {content}
    </Link>
  ) : (
    <span role="img" aria-label={label} className="d-inline-flex flex-items-center gap-1">
      {content}
    </span>
  )
}

export const PullsListItem = memo(function PullsListItemComponent({
  as,
  role,
  pull,
  showUnreadIndicator = true,
  showUnreadBorder = true,
  isSelected,
  onSelect,
  onSelectRow,
  onFilterSelect,
  onReviewFilterSelect,
  isDeferredLoading,
  isAgentAttributionEnabled,
  sortingItemSelected,
  showRepository = true,
  titleLinkProps,
  getAuthorLinkProps,
  declutter = false,
  isUpdatedSort = false,
}: PullsListItemProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {variant} = useListViewVariant()
  const isCompact = variant === 'compact'
  const {isBrowser} = useRuntimeEnvironment()

  const commitChecksDialogEnabled = isBrowser
  const compactAssigneeOverflowEnabled = isFeatureEnabled('issues_pulls_dashboard_assignee_overflow')
  const hasInlineChecksBadge =
    !isCompact && commitChecksDialogEnabled && Boolean(pull.statusCheckRollup && pull.headSha)
  const attributedUsername = isAgentAttributionEnabled ? deriveAttributedUsername(pull) : undefined
  const isPullAgentAuthored = isAgentAttributionEnabled ? isAgentAuthor(pull) : false
  const isQueued = pull.displayState === DisplayStateType.Queued
  const showDraftLabel = pull.isDraft && (!declutter || pull.displayState !== DisplayStateType.Draft)
  const reviewFilter =
    pull.reviewDecisionState === ReviewDecisionState.Approved
      ? 'approved'
      : pull.reviewDecisionState === ReviewDecisionState.ReviewRequired
        ? 'required'
        : pull.reviewDecisionState === ReviewDecisionState.ChangesRequested
          ? 'changes_requested'
          : undefined
  const reviewFilterProps =
    onReviewFilterSelect && reviewFilter
      ? {onClick: () => onReviewFilterSelect(reviewFilter), className: styles.filterLink}
      : undefined

  const reactionSortDisplay = getReactionSortDisplay(sortingItemSelected)
  // Decluttered rows only show "Updated" when the list is sorted by updated time, matching the
  // classic repo pulls list and the issues dashboard.
  const showUpdatedAt = !declutter || isUpdatedSort
  const reactionCount = reactionSortDisplay
    ? reactionSortDisplay.content
      ? (pull.reactionGroups?.find(group => group.content === reactionSortDisplay.content)?.totalCount ?? 0)
      : (pull.reactionGroups?.reduce((sum, group) => sum + group.totalCount, 0) ?? 0)
    : 0
  const linkedIssueMetadata =
    !isCompact && pull.linkedIssueCount && pull.linkedIssueCount > 0 ? (
      <LinkedIssueMetadataValue
        count={pull.linkedIssueCount}
        url={pull.linkedIssueUrl}
        pullRequestRepository={pull.repoNameWithOwner}
        pullRequestNumber={pull.number}
        pullRequestTitle={pull.title}
      />
    ) : null

  // `displayState` is optional in the payload (the backend derives it from a
  // nilable `PullRequest::Icon::State`), so guard the icon lookup: an unknown or
  // missing state would otherwise resolve to `undefined` and crash the row when
  // rendered. Fall back to the neutral open pull-request glyph, pairing with the
  // existing `PR_STATE_FALLBACK_TOOLTIP_LABEL`.
  const PRStateIcon =
    PullRequestStateIcons[pull.displayState as keyof typeof PullRequestStateIcons] ?? PullRequestStateIcons.OPEN
  const prStateTooltipLabel =
    PR_STATE_TOOLTIP_LABELS[pull.displayState as keyof typeof PullRequestStateIcons] ?? PR_STATE_FALLBACK_TOOLTIP_LABEL
  const [owner, repo] = pull.repoNameWithOwner.split('/')
  const authorBadgeContent = pull.author.identity ? (
    isPullAgentAuthored && pull.author.identity === 'AI' ? null : (
      <Label variant="secondary">{AUTHOR_IDENTITY_LABELS[pull.author.identity]}</Label>
    )
  ) : pull.author.association && pull.author.association !== 'NONE' ? (
    <CommentAuthorAssociation association={pull.author.association} org={owner ?? ''} repo={repo ?? ''} />
  ) : null
  const authorBadge = authorBadgeContent ? <span className={styles.metadataGroup}>{authorBadgeContent}</span> : null
  const hovercardAttributes = getHovercardAttributesForType('pull_request', {
    owner,
    repo,
    pullRequestNumber: pull.number,
  })
  const onTitleClick = () => {
    onSelectRow?.()
    sendAnalyticsEvent('pull-requests.click', 'LIST_ITEM_TITLE', {displayState: pull.displayState})
  }
  const labelBadges = !isCompact
    ? renderLabelBadges({
        labels: pull.labels,
        onSelect: onFilterSelect ? label => onFilterSelect('label', label) : undefined,
        filterLinkClassName: styles.filterLink,
      })
    : undefined

  const compactRepoName =
    onFilterSelect && showRepository ? (
      <button
        type="button"
        className={styles.filterLink}
        onClick={() => onFilterSelect('repo', pull.repoNameWithOwner)}
        data-testid="repo-filter-link"
        aria-label={`Filter by repository ${pull.repoNameWithOwner}`}
      >
        {pull.repoNameWithOwner}
      </button>
    ) : showRepository ? (
      <span>{pull.repoNameWithOwner}</span>
    ) : (
      <span>#{pull.number}</span>
    )

  const renderAuthorLink = (login: string, testId: string, displayName?: string) => (
    <AuthorFilterControl
      login={login}
      displayName={displayName}
      testId={testId}
      onAuthorClick={onFilterSelect ? author => onFilterSelect('author', author) : undefined}
      authorLinkProps={getAuthorLinkProps?.(login)}
    >
      {displayName || login}
    </AuthorFilterControl>
  )

  const compactAuthor = (
    <>
      {attributedUsername ? (
        <>
          {renderAuthorLink(attributedUsername, 'attributed-author-filter-link')}
          <span> with </span>
        </>
      ) : null}
      {renderAuthorLink(pull.author.displayLogin, 'author-filter-link', pull.author.displayName)}
      {isPullAgentAuthored ? (
        <Label variant="secondary" className="ml-1">
          AI
        </Label>
      ) : null}
    </>
  )

  const compactStatus =
    !isQueued && pull.statusCheckRollup ? (
      commitChecksDialogEnabled && pull.headSha ? (
        <PullsChecksStatusBadge
          headSha={pull.headSha}
          repositoryNwo={pull.repoNameWithOwner}
          statusCheckRollup={pull.statusCheckRollup}
          aliveChannel={pull.commitHeadShaChannel}
        />
      ) : (
        <CompactStatusCheckDisplay statusCheckRollup={pull.statusCheckRollup} />
      )
    ) : null

  const metadata = isCompact ? (
    <>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataUtility)}>
        {renderCompactMetadataValue(
          pull.stackPosition != null && pull.stackSize != null ? (
            <StackBadge
              pullRequestNumber={pull.number}
              stackPosition={pull.stackPosition}
              stackSize={pull.stackSize}
              repoNameWithOwner={pull.repoNameWithOwner}
              appearance="link"
            />
          ) : null,
        )}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataReview)}>
        {renderCompactMetadataValue(
          !isQueued && isDeferredLoading ? (
            <DeferredCompactMetadataSkeleton kind="review" width="16px" />
          ) : !isQueued && pull.reviewDecisionState ? (
            <CompactReviewDecision
              reviewDecisionState={pull.reviewDecisionState}
              counts={{
                approvedReviewsCount: pull.approvedReviewsCount,
                changesRequestedReviewsCount: pull.changesRequestedReviewsCount,
              }}
              filterProps={reviewFilterProps}
            />
          ) : null,
        )}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataStatus)}>
        {renderCompactMetadataValue(
          !isQueued && isDeferredLoading ? (
            <DeferredCompactMetadataSkeleton kind="status" width="40px" />
          ) : (
            compactStatus
          ),
        )}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataUtility)}>
        {renderCompactMetadataValue(
          pull.commentCount > 0 ? (
            <>
              <CommentIcon aria-label={`${pull.commentCount} comments`} size={16} /> {pull.commentCount}
            </>
          ) : null,
        )}
      </ListItemMetadata>
      {reactionSortDisplay ? (
        <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataUtility)}>
          {renderCompactMetadataValue(
            reactionCount > 0 ? (
              <>
                <span
                  role="img"
                  aria-label={`${reactionCount} ${reactionSortDisplay.label} reaction${reactionCount === 1 ? '' : 's'}`}
                >
                  {reactionSortDisplay.emoji ?? <SmileyIcon aria-hidden="true" />}
                </span>{' '}
                <span aria-hidden="true">{reactionCount}</span>
              </>
            ) : null,
          )}
        </ListItemMetadata>
      ) : null}
      <ListItemMetadata className={clsx(compactStyles.metadataCell, compactStyles.metadataAssignees)}>
        {renderCompactMetadataValue(
          pull.assignees && pull.assignees.length > 0 ? (
            <AssigneeAvatars
              assignees={pull.assignees}
              onSelect={onFilterSelect ? login => onFilterSelect('assignee', login) : undefined}
              filterLinkClassName={styles.assigneeFilterButton}
              maxVisible={compactAssigneeOverflowEnabled ? 2 : undefined}
            />
          ) : null,
        )}
      </ListItemMetadata>
    </>
  ) : (
    <>
      {linkedIssueMetadata ? <ListItemMetadata>{linkedIssueMetadata}</ListItemMetadata> : null}
      {pull.commentCount > 0 ? (
        <ListItemMetadata>
          <CommentIcon size={16} /> {pull.commentCount}
        </ListItemMetadata>
      ) : null}
      {reactionSortDisplay && reactionCount > 0 ? (
        <ListItemMetadata>
          <span
            role="img"
            aria-label={`${reactionCount} ${reactionSortDisplay.label} reaction${reactionCount === 1 ? '' : 's'}`}
          >
            {reactionSortDisplay.emoji ?? <SmileyIcon aria-hidden="true" />}
          </span>{' '}
          <span aria-hidden="true">{reactionCount}</span>
        </ListItemMetadata>
      ) : null}
      {pull.assignees && pull.assignees.length > 0 && (
        <ListItemMetadata alignment="right">
          <AssigneeAvatars
            assignees={pull.assignees}
            onSelect={onFilterSelect ? login => onFilterSelect('assignee', login) : undefined}
            filterLinkClassName={styles.assigneeFilterButton}
            alignRight
          />
        </ListItemMetadata>
      )}
    </>
  )

  return (
    <ListItem
      as={as}
      role={role}
      key={pull.id}
      isSelected={isSelected}
      onSelect={onSelect}
      className={unreadRowClassName(
        !showUnreadIndicator || !showUnreadBorder || pull.isReadByCurrentUser,
        isCompact ? styles.listItemCompact : styles.listItem,
        isSelected && styles.selectedRow,
      )}
      title={
        <ListItemTitle
          href={pull.permalink}
          onClick={onTitleClick}
          value={pull.titleHtml ? <VerifiedHTMLText as="span" html={pull.titleHtml} /> : <span>{pull.title}</span>}
          linkProps={{
            ...titleLinkProps,
            ...hovercardAttributes,
            href: pull.permalink,
            onClick: onTitleClick,
            'data-testid': 'listitem-title-link',
          }}
          anchorClassName={clsx(styles.titleLink, inlineCodeStyles.inlineCodeTitle)}
          trailingBadges={labelBadges}
        />
      }
      metadata={metadata}
      metadataContainerClassName={isCompact ? compactStyles.metadataContainer : undefined}
    >
      <ListItemLeadingContent>
        {showUnreadIndicator ? <UnreadRowLabel isRead={pull.isReadByCurrentUser} /> : null}
        <ListItemLeadingVisual>
          <NonInteractiveTooltip label={prStateTooltipLabel} direction="e" includeAriaAttributes={false}>
            <PRStateIcon aria-label={prStateTooltipLabel} />
          </NonInteractiveTooltip>
        </ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent
        className={clsx(
          hasInlineChecksBadge && styles.inlineChecksMainContent,
          authorBadge && styles.authorAssociationMainContent,
        )}
      >
        {isCompact ? (
          <ListItemDescription
            className={clsx(compactStyles.leftMetadata, authorBadge && styles.authorAssociationDescription)}
          >
            <span className={compactStyles.leftMetadataText}>{compactRepoName}</span>
            <span className={compactStyles.leftMetadataSeparator} aria-hidden="true">
              •
            </span>
            <span className={compactStyles.leftMetadataText}>{compactAuthor}</span>
            {authorBadge}
            {showDraftLabel ? (
              <>
                <span className={compactStyles.leftMetadataSeparator} aria-hidden="true">
                  •
                </span>
                <span className={compactStyles.leftMetadataText}>Draft</span>
              </>
            ) : null}
            {isQueued ? (
              <>
                <span className={compactStyles.leftMetadataSeparator} aria-hidden="true">
                  •
                </span>
                <span className={compactStyles.leftMetadataText}>
                  <QueuedState mergeQueueUrl={pull.mergeQueueUrl} />
                </span>
              </>
            ) : null}
          </ListItemDescription>
        ) : (
          <ListItemDescription className={styles.description}>
            <span>
              {showRepository ? compactRepoName : null}
              <span>#{pull.number}</span>
            </span>
            <PullsListItemTimestampContainer
              createdAt={new Date(pull.createdAt)}
              updatedAt={new Date(pull.updatedAt)}
              closedAt={pull.closedAt ? new Date(pull.closedAt) : undefined}
              mergedAt={pull.mergedAt ? new Date(pull.mergedAt) : undefined}
              prState={pull.state}
              author={pull.author.displayLogin}
              authorDisplayName={pull.author.displayName}
              isAgent={isPullAgentAuthored}
              attributedUsername={attributedUsername}
              onAuthorClick={onFilterSelect ? author => onFilterSelect('author', author) : undefined}
              getAuthorLinkProps={getAuthorLinkProps}
              showUpdatedAt={showUpdatedAt}
            />
            {authorBadge}
            {showDraftLabel ? (
              <span className={styles.metadataGroup}>
                <Middot />
                Draft
              </span>
            ) : null}
            {isQueued ? (
              <span className={styles.metadataGroup}>
                <Middot />
                <QueuedState mergeQueueUrl={pull.mergeQueueUrl} />
              </span>
            ) : null}
            {pull.stackPosition != null && pull.stackSize != null ? (
              <span className={styles.metadataGroup}>
                <Middot />
                <StackBadge
                  pullRequestNumber={pull.number}
                  stackPosition={pull.stackPosition}
                  stackSize={pull.stackSize}
                  repoNameWithOwner={pull.repoNameWithOwner}
                  size={14}
                  appearance="link"
                />
              </span>
            ) : null}
            {!isQueued && pull.reviewDecisionState ? (
              <ReviewDecision
                reviewDecisionState={pull.reviewDecisionState}
                counts={{
                  approvedReviewsCount: pull.approvedReviewsCount,
                  changesRequestedReviewsCount: pull.changesRequestedReviewsCount,
                }}
                filterProps={reviewFilterProps}
                showIcon={false}
              />
            ) : !isQueued && isDeferredLoading ? (
              <DeferredMetadataSkeleton kind="review" width="100px" />
            ) : null}
            {!isQueued && pull.statusCheckRollup ? (
              commitChecksDialogEnabled && pull.headSha ? (
                <span className={styles.inlineChecksBadge}>
                  <Middot />
                  <PullsChecksStatusBadge
                    headSha={pull.headSha}
                    repositoryNwo={pull.repoNameWithOwner}
                    statusCheckRollup={pull.statusCheckRollup}
                    aliveChannel={pull.commitHeadShaChannel}
                    buttonClassName={clsx('color-fg-muted', styles.alignedChecksBadge)}
                    buttonVariant="link"
                  />
                </span>
              ) : (
                <StatusCheckDisplay statusCheckRollup={pull.statusCheckRollup} />
              )
            ) : !isQueued && isDeferredLoading ? (
              <DeferredMetadataSkeleton kind="status" width="28px" />
            ) : null}
            {pull.milestone ? (
              <span className={styles.metadataGroup}>
                <Middot />
                <Link href={pull.milestone.url} muted aria-label={`Milestone: ${pull.milestone.title}`}>
                  <MilestoneIcon size={16} /> {pull.milestone.title}
                </Link>
              </span>
            ) : null}
          </ListItemDescription>
        )}
      </ListItemMainContent>
    </ListItem>
  )
})
